import { useEffect } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { Pencil } from 'lucide-react'
import { BuilderProvider } from './BuilderContext'
import { BuilderHeader } from './BuilderHeader'
import { BuilderCanvas } from './BuilderCanvas'
import { BuilderDryRunPanel } from './BuilderDryRunPanel'
import { useBuilder } from './hooks/useBuilder'
// Boot the editor plugin registry. Importing this once registers all
// built-in editors (text, image-drupal, link, list, …). Project-level custom
// editors can be added later by calling registerEditor() after this import.
import './editors'

// Node bundles the builder can host as a dashboard (content = a `field_layout`
// JSON blob edited via the decoupled API).
const DASHBOARD_HOST_BUNDLES = ['dashboard']

export const BuilderShell = ({ hostKind: routeHostKind = null, hostId: propHostId = null, slug: propSlug = null, readOnly = false, embedded = false, hosted = false, showBar = true, onEdit = null }) => {
  const { slug: routeSlug, id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  // `propSlug` : monté HORS route — le wiki héberge la page du builder dans son
  // propre shell (`BuilderPageMount`), l'adresse reste `/docs/page/<nid>` et le
  // slug vient donc d'une donnée, pas de l'URL. Même patron que `propHostId`
  // pour les tableaux de bord.
  const slug = propSlug || routeSlug

  // `?edit=1` reste accepté (création, duplication, « Modifier » depuis une
  // liste de contenus) mais n'est plus la CONDITION du mode édition : entrer
  // dans le builder, c'est venir éditer. Toute route non `readOnly`
  // (`/builder/<slug>`, `/builder/dashboards/<id>`) ouvre donc directement
  // l'édition — l'aperçu se prend au bouton œil de l'en-tête. Le paramètre
  // reste lu pour le nettoyage d'URL plus bas.
  const autoEdit = searchParams.get('edit') === '1'
  // `?clone=1` : ouvrir d'emblée la modale de duplication (bouton « Dupliquer »
  // d'une liste de contenus, qui n'a pas de quoi appeler l'API de duplication
  // elle-même — c'est le builder qui la porte).
  const autoClone = searchParams.get('clone') === '1'

  // Un nœud « tableau de bord » (contenu = un blob `field_layout`) est piloté par
  // la ROUTE : `/builder/dashboards/<id>` (édition) et `/dashboard/<id>` (vue
  // publique) passent `hostKind='dashboard'`, avec `<id>` = le slug portable OU le
  // nid. Repli rétrocompatible : l'ancienne forme `?host=<bundle>&id=<nid>`.
  const legacyHostKind = searchParams.get('host')
  const legacyHostId = searchParams.get('id') || searchParams.get('nid')
  const hostKind = routeHostKind || (DASHBOARD_HOST_BUNDLES.includes(legacyHostKind) ? legacyHostKind : null)
  // `propHostId` : monté HORS route (cf. `DashboardMount`, qui rend la page
  // d'accueil choisie par l'utilisateur) — l'identifiant vient alors d'une
  // donnée, pas de l'URL. Il prime sur le paramètre de route, qui n'existe pas
  // dans ce cas.
  const hostId = routeHostKind ? (propHostId || id || searchParams.get('nid')) : legacyHostId
  const initialHost = hostKind && hostId ? { kind: hostKind, id: hostId } : null

  // Le canvas dispose les blocs en pile verticale ; la grille 2-D est fournie par
  // un bloc `GridLayout`. `?layout=grid` dispose plutôt les blocs de premier
  // niveau dans une grille.
  const initialLayoutMode = searchParams.get('layout') === 'grid' ? 'grid' : 'linear'

  // Identité de ce qui est monté. React réutilise l'instance d'un composant
  // rendu par DEUX routes voisines (ici `/dashboards/<id>` en lecture seule et
  // `/builder/dashboards/<id>?edit=1`) : sans clé, le provider n'est pas
  // remonté, `initialEditMode` — lu une seule fois par `useState` — reste à sa
  // valeur d'origine, et « Éditer » rouvrait le tableau en aperçu. La clé
  // change avec le mode d'accès et la cible → remontage propre (état d'édition,
  // brouillons et `isDirty` repartent de zéro).
  const mountKey = `${readOnly ? 'view' : 'edit'}:${hostKind || 'page'}:${hostId || slug || ''}`

  // Nettoyer le paramètre de l'URL pour éviter qu'un refresh ultérieur remette
  // le mode édition. Dépend de `autoEdit`/`autoClone` : sur la navigation
  // interne décrite ci-dessus, ce composant n'est pas remonté — un effet de
  // montage seul laisserait `?edit=1` dans l'URL.
  useEffect(() => {
    if (autoEdit || autoClone) {
      // Ne retirer QUE les paramètres du chrome de l'éditeur. `setSearchParams({})`
      // vidait toute la query string : les filtres partagés d'un tableau de bord
      // (`?provider=anthropic…`, cf. `UrlSync`) disparaissaient au premier rendu,
      // sans erreur ni trace — un lien filtré ouvert en édition perdait ses filtres.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('edit')
        next.delete('clone')
        return next
      }, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit, autoClone])

  return (
    <BuilderProvider key={mountKey} slug={slug} initialEditMode={!readOnly} initialLayoutMode={initialLayoutMode} initialHost={initialHost} readOnly={readOnly}>
      {/* Aucune hauteur imposée, aucun conteneur défilant : le builder pousse la
          page et c'est le DOCUMENT qui défile — comme les autres écrans du
          builder (`BuilderHome`, `BuilderTypeShell`). Un `h-screen` + un
          `overflow-y-auto` interne donnaient un second ascenseur imbriqué sous
          le menu orbit, et cassaient l'en-tête `sticky` (calée sous le menu,
          donc hors de tout scroller). */}
      <div id="builder-shell" className={`${embedded ? 'flex-1' : 'min-h-screen'} flex flex-col bg-gray-50`}>
        {/* `hosted` : la barre est montée DANS un autre écran (le wiki, cf.
            BuilderPageMount), pas par une route du builder. Elle y perd ce qui
            n'a pas d'objet — retour à la liste, site, adresse, métadonnées,
            modèles — et s'autorise le retour à la ligne plutôt que d'écraser le
            champ titre dans une colonne étroite.
            À ne pas confondre avec `embedded`, que le ROUTEUR passe dès qu'un
            écran est rendu avec le menu Orbit (App.jsx : `embedded={route.withMenu}`) :
            il ne dit rien de l'hôte, seulement de la hauteur à prendre. */}
        {showBar && (readOnly ? <PublicDashboardBar editId={hostId} onEdit={onEdit} /> : <BuilderHeader initialClone={autoClone} hosted={hosted} />)}
        <BuilderCanvas slug={slug} />
        {/* Envoi à blanc : en aperçu, un formulaire ne déclenche pas son flux. Le
            builder ÉDITABLE seulement — une vue en lecture seule (tableau de bord
            public) envoie pour de vrai, comme une page publique. */}
        {!readOnly && <BuilderDryRunPanel />}
      </div>
    </BuilderProvider>
  )
}

/**
 * Barre minimale de la vue en LECTURE : le nom de la page + son bouton
 * « Modifier ».
 *
 * Deux façons d'entrer en édition, selon la façon dont la page est montée :
 *  - par la ROUTE (`/dashboard/<id>`) — on NAVIGUE vers l'éditeur du builder ;
 *  - montée DANS un autre écran (`onEdit` fourni : le wiki, qui garde son
 *    adresse `/docs/page/<nid>` et sa colonne de gauche) — on bascule SUR PLACE,
 *    en écrivant dans le magasin de l'hôte. Sans ce cas, le lien pointait sur
 *    `/builder/dashboards/null` : il n'y a pas d'`editId` pour une page montée
 *    par son slug.
 *
 * `canEdit` vient du back pour un tableau de bord (il masque le bouton pour un
 * visiteur anonyme d'un dashboard public). Une page montée par un hôte n'a pas
 * de `hostMeta` : c'est alors la présence d'`onEdit` qui fait foi — l'hôte ne le
 * fournit que s'il a de quoi éditer.
 */
const PublicDashboardBar = ({ editId, onEdit = null }) => {
  const { page } = useBuilder()
  const canEdit = onEdit ? true : !!page?.hostMeta?.canEdit
  const className = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 transition-colors'
  return (
    <header className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
      <h1 className="text-base font-semibold text-gray-800 truncate">{page?.title || 'Tableau de bord'}</h1>
      {canEdit && (onEdit ? (
        <button type="button" onClick={onEdit} className={className}>
          <Pencil size={14} aria-hidden="true" />
          Modifier
        </button>
      ) : (
        <Link to={`/builder/dashboards/${editId}?edit=1`} className={className}>
          <Pencil size={14} aria-hidden="true" />
          Éditer
        </Link>
      ))}
    </header>
  )
}
